/* nt-favorites.js — Favorites on every product surface (Justin 2026-10-22)
 * ------------------------------------------------------------------
 * Hearts on: product pages (next to h1.product__title / h1.bsv4__title), collection cards
 * (.product-item, .nthp-card), and both quick-buy modals. Tapping saves the product itself (no
 * colour / variant) and shows a "Saved to Favorites · View · Undo" toast.
 *
 * Storage: the customer's account (metafield custom.favorites) through the Shopify app proxy
 * /apps/favorites (Lambda ninja-favorites verifies Shopify's signature + logged_in_customer_id).
 * Liquid prints the saved list into window.__ntFav (layout/theme.liquid) so hearts paint filled
 * on first render. Until the proxy answers, favorites live in localStorage (ntFav:<cid>), and a
 * local list is pushed to the account once it does. Guests: tapping a heart sends them to sign
 * in, and the pending heart is saved right after (ntFavPending).
 * The Favorites page = templates/collection.favorites.json (sections/nt-favorites-page.liquid).
 */
(function () {
  'use strict';
  if (window.NTFav) return;

  var CFG = window.__ntFav || { cid: null, items: [] };
  var CID = CFG.cid ? String(CFG.cid) : '';
  var PROXY = '/apps/favorites';
  var LS_KEY = 'ntFav:' + (CID || 'guest');
  var PENDING_KEY = 'ntFavPending';
  var FAV_URL = '/pages/welcome?tab=my-favorites';
  var items = Array.isArray(CFG.items) ? CFG.items.slice() : [];
  var remote = null;          // null = unknown, true = proxy works, false = local only
  var listeners = [];
  var DESKTOP = window.matchMedia('(min-width: 750px)');   /* desktop product-page pill: inline note, no toast */
  /* Labs Favorites report (Owen 2026-10-23): one fire-and-forget beacon per real add / remove —
     product, surface, customer. text/plain sendBeacon = no preflight, never awaited, never throws. */
  var LABS_FAV_URL = 'https://labs-admin.ninjaondemand.com/api/favorite';
  var SURFACES = /^(pdp|img|card|modal|signin|undo|page|other)$/;
  /* `a` (optional) = which button on the My Favorites page; a `view` carries no product (p 0) */
  function labsFav(op, item, src, a) {
    try {
      if (!CID || !item || (!item.p && op !== 'view')) return;
      var env = window.Shopify && Shopify.theme && Shopify.theme.role === 'main' ? 'production' : 'staging';
      var o = { op: op, p: Number(item.p) || 0, h: String(item.h || '').slice(0, 255), s: SURFACES.test(src || '') ? src : 'other', c: CID, env: env };
      if (a) o.a = a;
      var body = JSON.stringify(o);
      var blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon) navigator.sendBeacon(LABS_FAV_URL, blob);
      else fetch(LABS_FAV_URL, { method: 'POST', body: blob, keepalive: true }).catch(function () {});
    } catch (e) { /* never touch the page */ }
  }

  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function lsDel(k) { try { localStorage.removeItem(k); } catch (e) {} }

  /* The ACCOUNT is the source of truth (Liquid printed it into __ntFav). localStorage is only a
     mirror, plus a one-time hand-over of hearts saved before the account was connected — never
     merged again, or a heart removed elsewhere would come back (Justin 2026-10-22). */
  var MIGRATED_KEY = 'ntFavMigrated:' + (CID || 'guest');
  var local = lsGet(LS_KEY);
  if (!CID && Array.isArray(local)) items = local;

  function has(pid) { pid = Number(pid); return items.some(function (x) { return x.p === pid; }); }
  function count() { return items.length; }
  function emit() {
    lsSet(LS_KEY, items);
    paintAll();
    listeners.forEach(function (fn) { try { fn(items); } catch (e) {} });
    try { document.dispatchEvent(new CustomEvent('ntfav:change', { detail: { items: items } })); } catch (e) {}
  }

  function call(op, item) {
    if (!CID) return Promise.reject(new Error('guest'));
    var opts = { method: op === 'get' ? 'GET' : 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (item) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify({ item: item }); }
    return fetch(PROXY + '?op=' + op, opts).then(function (r) {
      var ct = r.headers.get('content-type') || '';
      if (!r.ok || ct.indexOf('json') === -1) throw new Error('proxy ' + r.status);
      return r.json();
    });
  }

  function syncFromServer() {
    if (!CID) return Promise.resolve();
    return call('get').then(function (j) {
      remote = true;
      items = Array.isArray(j.items) ? j.items : [];
      emit();
    }, function () { remote = false; });
  }

  var ready = (CID ? call('get').then(function (j) {
    remote = true;
    var server = Array.isArray(j.items) ? j.items : [];
    items = server;
    if (lsGet(MIGRATED_KEY) || !Array.isArray(local)) { lsSet(MIGRATED_KEY, 1); return; }
    lsSet(MIGRATED_KEY, 1);
    var extra = local.filter(function (x) { return x && x.p && !server.some(function (s) { return s.p === x.p; }); });
    return extra.reduce(function (p, it) { return p.then(function () { return call('add', it).then(function (j2) { items = j2.items || items; }); }); }, Promise.resolve());
  }, function () { remote = false; if (Array.isArray(local) && local.length && !items.length) items = local; }) : Promise.resolve()).then(function () { emit(); flushPending(); });

  /* Back/forward restores a frozen page, and other tabs change the list: re-read the account */
  window.addEventListener('pageshow', function (e) { if (e.persisted) syncFromServer(); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible' && remote) syncFromServer(); });
  window.addEventListener('storage', function (e) {
    if (e.key !== LS_KEY) return;
    try { var v = JSON.parse(e.newValue || '[]'); if (Array.isArray(v)) { items = v; paintAll(); listeners.forEach(function (fn) { try { fn(items); } catch (x) {} }); } } catch (x) {}
  });

  function add(item, opts) {
    item = { p: Number(item.p), h: String(item.h || ''), v: item.v ? Number(item.v) : null, c: item.c || null, t: Date.now() };
    if (!item.p) return;
    if (!CID) { item.r = location.pathname + location.search; item.at = Date.now(); lsSet(PENDING_KEY, item); goSignIn(); return; }
    items = [item].concat(items.filter(function (x) { return x.p !== item.p; }));
    emit();
    labsFav('add', item, opts && opts.src);
    if (!(opts && (opts.silent || (opts.inline && DESKTOP.matches)))) toast(item);
    if (remote !== false) call('add', item).then(function (j) { remote = true; items = j.items || items; emit(); }, function () { remote = false; });
  }
  function remove(pid, src) {
    pid = Number(pid);
    var it = items.filter(function (x) { return x.p === pid; })[0];
    items = items.filter(function (x) { return x.p !== pid; });
    emit();
    if (it) labsFav('remove', it, src);
    if (it && remote !== false) call('remove', it).then(function (j) { items = j.items || items; emit(); }, function () {});
    return it;
  }
  function toggle(item, opts) { if (has(item.p)) { remove(item.p, opts && opts.src); return false; } add(item, opts); return true; }

  function goSignIn() {
    var back = location.pathname + location.search;
    /* new customer accounts: /account/login bounces to account.ninjatransfers.com and drops every param, so
       the guest landed on the account page. /customer_authentication/login?return_to= is the route the rest
       of the theme uses (studio, reorder, app home); Shopify carries return_to through sign-in. */
    location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(back);
  }
  function flushPending() {
    var p = lsGet(PENDING_KEY);
    if (!p || !CID) return;
    /* back to the page the guest tapped the heart on (Owen 2026-10-23): Shopify's sign-in can land them
       elsewhere (account page / portal), so the first signed-in page bounces once to the saved path and the
       favorite saves THERE (no half-sent save, and they see it saved). Same-site paths only, 30 min, once. */
    var r = typeof p.r === 'string' && /^\/(?!\/)/.test(p.r) ? p.r : '';
    if (r && !p.bounced && Date.now() - (Number(p.at) || 0) < 1800000 && r.split('?')[0] !== location.pathname && window.top === window.self) {
      p.bounced = 1; lsSet(PENDING_KEY, p);
      location.replace(r);
      return;
    }
    lsDel(PENDING_KEY);
    add(p, { src: 'signin' });
  }

  /* ---------------- toast ---------------- */
  var toastEl = null, toastTimer = null;
  function toast(item) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ntfav-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '<span class="ntfav-toast__heart" aria-hidden="true">' + HEART_FILLED + '</span>' +
      '<span class="ntfav-toast__text">Saved to Favorites</span>' +
      '<a class="ntfav-toast__view" href="' + FAV_URL + '" data-portal-tab="My Favorites">View</a>' +
      '<button type="button" class="ntfav-toast__undo">Undo</button>';
    toastEl.querySelector('.ntfav-toast__undo').onclick = function () { remove(item.p, 'undo'); hideToast(); };
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4500);
  }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-on'); }

  /* ---------------- hearts ---------------- */
  var HEART = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 21s-7.5-4.6-9.5-9.3C1.1 8.4 3.2 5 6.6 5c2 0 3.6 1.1 4.4 2.6h2C13.8 6.1 15.4 5 17.4 5c3.4 0 5.5 3.4 4.1 6.7C19.5 16.4 12 21 12 21z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>';
  var HEART_FILLED = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 21s-7.5-4.6-9.5-9.3C1.1 8.4 3.2 5 6.6 5c2 0 3.6 1.1 4.4 2.6h2C13.8 6.1 15.4 5 17.4 5c3.4 0 5.5 3.4 4.1 6.7C19.5 16.4 12 21 12 21z" fill="#EF4444" stroke="#EF4444" stroke-width="1.8" stroke-linejoin="round"/></svg>';

  function makeBtn(kind, data) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ntfav-btn ntfav-btn--' + kind;
    b.setAttribute('data-ntfav-pid', data.p);
    if (data.h) b.setAttribute('data-ntfav-handle', data.h);
    b.innerHTML = '<span class="ntfav-btn__icon"></span>' + (kind === 'pdp' ? '<span class="ntfav-btn__label">Favorite</span>' : '');
    paint(b);
    return b;
  }
  function paint(b) {
    var on = has(b.getAttribute('data-ntfav-pid'));
    if (b.getAttribute('data-ntfav-on') === (on ? '1' : '0')) return;   /* no DOM write = no observer loop */
    b.setAttribute('data-ntfav-on', on ? '1' : '0');
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.setAttribute('aria-label', on ? 'Remove from Favorites' : 'Add to Favorites');
    b.querySelector('.ntfav-btn__icon').innerHTML = on ? HEART_FILLED : HEART;
    var lab = b.querySelector('.ntfav-btn__label');
    if (lab) lab.textContent = on ? 'Favorited' : 'Favorite';
  }
  function paintAll() {
    document.querySelectorAll('.ntfav-btn').forEach(paint);
    document.querySelectorAll('[data-ntfav-note]').forEach(function (n) {
      var on = has(n.getAttribute('data-ntfav-note'));
      if (n.classList.contains('is-on') !== on) n.classList.toggle('is-on', on);
    });
    document.querySelectorAll('[data-nt-fav-badge]').forEach(function (el) {
      var n = String(count()), txt = count() ? n : '';
      if (el.getAttribute('data-count') !== n) el.setAttribute('data-count', n);
      if (el.textContent !== txt) el.textContent = txt;
    });
  }

  function onClick(e) {
    var b = e.target.closest && e.target.closest('.ntfav-btn');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = b.getAttribute('data-ntfav-pid');
    /* the product itself — no colour / variant (Justin 2026-10-22) */
    var lab0 = b.querySelector('.ntfav-btn__label'), w0 = lab0 ? lab0.offsetWidth : 0;
    var src = (b.className.match(/ntfav-btn--(pdp|img|card|modal)/) || [])[1];
    toggle({ p: pid, h: b.getAttribute('data-ntfav-handle') }, { inline: b.classList.contains('ntfav-btn--pdp'), src: src });
    if (CID) pop(b, has(pid), w0);   /* guests are on their way to sign in */
  }
  /* click animation (Owen 2026-10-22) — a 1:1 port of the artifact claude.ai/artifact/JusvbCEsN6zSZDJm5sHfif:
     heart pop + ring + dot burst on save, a small shrink on remove; on the desktop pill the label slides
     Favorite <-> Favorited with the width easing between them, and the saved note fades in / out.
     Click-only on purpose: paint() also runs on account / cross-tab syncs, which stay still. */
  var FX_COLORS = ['#EF4444', '#019AFF', '#FFB020', '#EF4444', '#8B5CF6', '#019AFF'];
  var FX_DOTS = '';
  for (var fi = 0; fi < 10; fi++) FX_DOTS += '<i style="--a:' + (fi * 36) + 'deg;--c:' + FX_COLORS[fi % 6] + '"></i>';
  function pop(b, on, w0) {
    b.classList.remove('ntfav-pop', 'ntfav-unpop');
    var icon = b.querySelector('.ntfav-btn__icon');
    var old = icon && icon.querySelector('.ntfav-fx');
    if (old) old.remove();
    void b.offsetWidth;   /* restart the keyframes on a quick re-click */
    if (on && icon) {
      var fx = document.createElement('span');
      fx.className = 'ntfav-fx';
      fx.setAttribute('aria-hidden', 'true');
      fx.innerHTML = '<span class="ntfav-fx__ring"></span>' + FX_DOTS;
      icon.appendChild(fx);
    }
    b.classList.add(on ? 'ntfav-pop' : 'ntfav-unpop');
    var lab = b.querySelector('.ntfav-btn__label');
    if (lab) {
      lab.classList.add('ntfav-swap');
      lab.innerHTML = '<span class="ntfav-swap__in">' + (on ? 'Favorited' : 'Favorite') + '</span><span class="ntfav-swap__out">' + (on ? 'Favorite' : 'Favorited') + '</span>';
      var w1 = lab.querySelector('.ntfav-swap__in').offsetWidth;
      lab.style.width = w1 + 'px';
      if (w0 && lab.animate) lab.animate([{ width: w0 + 'px' }, { width: w1 + 'px' }], { duration: 300, easing: 'ease' });
    }
    /* desktop saved note: a quick preview after a save, then it fades and the pill stays red (Justin 2026-10-28) */
    var note = b.parentNode && b.parentNode.querySelector('.ntfav-note');
    if (note) {
      clearTimeout(note.__ntfavT);
      if (on) { note.classList.add('is-pop'); hideNoteSoon(note, 4000); }
      else { if (note.classList.contains('is-pop')) note.classList.add('ntfav-note--out'); note.classList.remove('is-pop'); }
    }
    clearTimeout(b.__ntfavPopT);
    b.__ntfavPopT = setTimeout(function () {
      b.classList.remove('ntfav-pop', 'ntfav-unpop');
      var f = b.querySelector('.ntfav-fx'); if (f) f.remove();
      if (lab) { lab.classList.remove('ntfav-swap'); lab.style.width = ''; lab.textContent = b.getAttribute('data-ntfav-on') === '1' ? 'Favorited' : 'Favorite'; }
      if (note) note.classList.remove('ntfav-note--out');
    }, 900);
  }
  function hideNoteSoon(note, ms) {
    clearTimeout(note.__ntfavT);
    note.__ntfavT = setTimeout(function () {
      if (!note.classList.contains('is-pop')) return;
      note.classList.add('ntfav-note--out');
      note.classList.remove('is-pop');
      note.__ntfavT = setTimeout(function () { note.classList.remove('ntfav-note--out'); }, 300);
    }, ms);
  }
  document.addEventListener('click', onClick, true);

  /* GA4: favorite_button_click on every heart (FAVTRACK 2026-10-23) — the .ntfav-btn hearts above plus the
     My Favorites page heart ([data-ntfp-remove], sections/nt-favorites-page.liquid). Event name + params are a
     pinned contract (the GA Chrome app reads them): do not rename. Bound on WINDOW capture so it runs before
     onClick toggles the state, which is what tells add from remove. Tracking only: no preventDefault, never
     throws, nothing happens when GA isn't loaded. One listener, so one hit per click. */
  function favSurface(b) {
    if (b.hasAttribute('data-ntfp-remove')) return 'favorites_page';
    if (b.classList.contains('ntfav-btn--modal')) return 'quick_view';
    if (b.classList.contains('ntfav-btn--pdp') || b.classList.contains('ntfav-btn--img')) return 'pdp';
    var body = document.body.classList;
    if (b.closest('#cart-recommendations') || body.contains('template-cart')) return 'cart';
    if (b.closest('#ai-search-page') || body.contains('template-search')) return 'search_card';
    if (b.closest('product-recommendations, .product-recommendations')) return 'recommendation';
    return 'collection_card';
  }
  function favTitle(b) {
    var el = b.hasAttribute('data-ntfp-remove') ? null
      : b.classList.contains('ntfav-btn--modal') ? (b.parentNode && b.parentNode.querySelector('.grt-product-modal__title, [data-grt-modal-title]'))
      : (b.classList.contains('ntfav-btn--pdp') || b.classList.contains('ntfav-btn--img')) ? document.querySelector('h1.product__title, h1.bsv4__title')
      : (b.closest('.product-item, .nthp-card') || document).querySelector('.product-item__title, .nthp-card__title');
    var t = el ? el.textContent : (b.getAttribute('data-ntfp-name') || '');
    return String(t || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }
  window.addEventListener('click', function (e) {
    try {
      var b = e.target && e.target.closest && e.target.closest('.ntfav-btn, [data-ntfp-remove]');
      if (!b || typeof gtag !== 'function') return;
      var pid = b.getAttribute('data-ntfav-pid') || b.getAttribute('data-ntfp-remove') || '';
      var handle = b.getAttribute('data-ntfav-handle') || '';
      if (!handle) items.some(function (x) { if (String(x.p) === String(pid)) { handle = x.h || ''; return true; } return false; });
      var action = 'unknown';
      try { if (Number(pid)) action = has(pid) ? 'remove' : 'add'; } catch (x) {}
      gtag('event', 'favorite_button_click', {
        product_id: String(pid),
        product_handle: handle,
        product_title: favTitle(b),
        action: action,
        surface: favSurface(b),
        logged_in: !!CID
      });
    } catch (x) {}
  }, true);

  function handleFromHref(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* product page: heart to the right of the title (pill on desktop, circle on phones) */
  function mountPdp() {
    var meta = (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) || null;
    var pid = meta && meta.id;
    if (!pid) {
      var pp = document.querySelector('product-page[data-id]');
      if (pp) pid = pp.getAttribute('data-id');
    }
    if (!pid) {
      try { pid = JSON.parse((document.getElementById('bsv4-config') || {}).textContent || '{}').productId; } catch (e) {}
    }
    if (!pid || !/^\/products\//.test(location.pathname)) return;
    var h1 = document.querySelector('h1.product__title, h1.bsv4__title');
    if (!h1) return;
    if (h1.parentNode.classList.contains('ntfav-titlewrap')) { mountPdpImage(pid); return; }
    var wrap = document.createElement('div');
    wrap.className = 'ntfav-titlewrap';
    /* by-size v4: the title block's "Hide after upload" toggle hides the heart + note with it */
    if (h1.classList.contains('bsv4-hide-uploaded')) wrap.classList.add('bsv4-hide-uploaded');
    h1.parentNode.insertBefore(wrap, h1);
    wrap.appendChild(h1);
    var col = document.createElement('div');
    col.className = 'ntfav-pdpcol';
    col.appendChild(makeBtn('pdp', { p: pid, h: handleFromHref(location.pathname) }));
    var note = document.createElement('p');
    note.className = 'ntfav-note';
    note.setAttribute('data-ntfav-note', pid);
    note.innerHTML = 'This has been saved to your <a href="' + FAV_URL + '" data-portal-tab="My Favorites">favorites</a>';
    /* the preview stays while the pointer is on it (its link is clickable) */
    note.addEventListener('mouseenter', function () { clearTimeout(note.__ntfavT); });
    note.addEventListener('mouseleave', function () { hideNoteSoon(note, 1500); });
    col.appendChild(note);
    wrap.appendChild(col);
    mountPdpImage(pid);
  }
  /* phones: the heart sits in the main product image's top-right corner instead (CSS swaps them) */
  function mountPdpImage(pid) {
    /* the gallery frame, not a slide (slides scroll away): v4 stage, else the main gallery's slider viewport */
    var host = document.querySelector('.bsv4-gallery__stage');
    if (!host) { var slide = document.querySelector('.product-gallery-item'); host = slide && (slide.closest('.css-slider-viewport') || slide.closest('css-slider') || slide.parentNode); }
    if (!host || host.querySelector('.ntfav-btn--img')) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(makeBtn('img', { p: pid, h: handleFromHref(location.pathname) }));
  }

  /* collection cards */
  function mountCards(root) {
    (root || document).querySelectorAll('.product-item[id^="product-item-"], .nthp-card[id^="product-item-"]').forEach(function (card) {
      if (card.querySelector('.ntfav-btn')) return;
      var pid = card.id.replace('product-item-', '');
      if (!/^\d+$/.test(pid)) return;
      var link = card.matches('a') ? card : card.querySelector('a[href*="/products/"]');
      var host = card.querySelector('.product-item__image, .nthp-card__media') || card;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(makeBtn('card', { p: pid, h: handleFromHref(link && link.getAttribute('href')) }));
    });
  }

  /* quick-buy modals: collection-card dialog (bh-modal-<id>) + global search modal */
  function mountModals(root) {
    (root || document).querySelectorAll('dialog[id^="bh-modal-"]').forEach(function (dlg) {
      var title = dlg.querySelector('.grt-product-modal__title');
      if (!title || dlg.querySelector('.ntfav-btn')) return;
      var pid = dlg.id.replace('bh-modal-', '');
      var link = dlg.querySelector('a[href*="/products/"]');
      var wrap = document.createElement('div');
      wrap.className = 'ntfav-titlewrap ntfav-titlewrap--modal';
      title.parentNode.insertBefore(wrap, title);
      wrap.appendChild(title);
      wrap.appendChild(makeBtn('modal', { p: pid, h: handleFromHref(link && link.getAttribute('href')) }));
    });
    var gTitle = document.querySelector('[data-grt-modal-title]');
    if (gTitle && !gTitle.parentNode.querySelector('.ntfav-btn')) {
      var gb = makeBtn('modal', { p: 0, h: '' });
      gb.hidden = true;
      gTitle.insertAdjacentElement('afterend', gb);
    }
  }
  /* the global modal fills its title per product: look the id up from the handle */
  var gSeen = '';
  function syncGlobalModal() {
    var linkEl = document.querySelector('[data-grt-modal-product-link]');
    var gb = document.querySelector('[data-grt-modal-title] ~ .ntfav-btn');
    if (!linkEl || !gb) return;
    var h = handleFromHref(linkEl.getAttribute('href'));
    if (!h || h === gSeen) return;
    gSeen = h;
    fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.json(); }).then(function (p) {
      gb.setAttribute('data-ntfav-pid', p.id);
      gb.setAttribute('data-ntfav-handle', h);
      gb.hidden = false;
      gb.removeAttribute('data-ntfav-on');
      paint(gb);
    }).catch(function () {});
  }

  /* hearts are for signed-in customers only (Justin 2026-10-28): a guest sees none */
  function scan() { if (CID) { mountPdp(); mountCards(); mountModals(); syncGlobalModal(); } paintAll(); }
  var scanQueued = false;
  function queueScan() { if (scanQueued) return; scanQueued = true; setTimeout(function () { scanQueued = false; scan(); }, 120); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  try { new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true }); } catch (e) {}

  window.NTFav = {
    items: function () { return items.slice(); }, has: has, add: add, remove: function (pid, src) { return remove(pid, src || 'page'); }, toggle: toggle,
    /* My Favorites page usage for the Labs report (Owen 2026-10-26): view | open | cart, from the page only */
    track: function (op, item, a) { if (/^(view|open|cart)$/.test(op)) labsFav(op, item || {}, 'page', a); }, count: count,
    ready: ready, onChange: function (fn) { listeners.push(fn); }, url: FAV_URL, remote: function () { return remote; }
  };
})();
